import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Truck, ShieldCheck, RotateCcw, Send } from 'lucide-react';
import { CONTACT_INFO, categories, CARD_DESCRIPTOR } from '../../data/mock';
import Logo from '../Logo';

const PaymentLogos = () => (
  <div className="flex flex-wrap items-center gap-2">
    <div className="h-7 w-12 bg-white rounded-md flex items-center justify-center px-1.5" title="Visa">
      <svg viewBox="0 0 48 32" className="h-5 w-auto" aria-label="Visa"><text x="24" y="22" textAnchor="middle" fontSize="14" fontWeight="900" fill="#1A1F71" fontFamily="Arial, sans-serif" fontStyle="italic">VISA</text></svg>
    </div>
    <div className="h-7 w-12 bg-white rounded-md flex items-center justify-center" title="Mastercard">
      <svg viewBox="0 0 48 32" className="h-5 w-auto" aria-label="Mastercard"><circle cx="19" cy="16" r="9" fill="#EB001B" /><circle cx="29" cy="16" r="9" fill="#F79E1B" /><path d="M24 9.5 a9 9 0 0 0 0 13" fill="#FF5F00" /></svg>
    </div>
    <div className="h-7 w-12 bg-[#2E77BB] rounded-md flex items-center justify-center px-1" title="American Express">
      <svg viewBox="0 0 48 32" className="h-5 w-auto" aria-label="American Express"><rect x="0" y="0" width="48" height="32" fill="#2E77BB" /><text x="24" y="14" textAnchor="middle" fontSize="6.5" fontWeight="900" fill="#FFFFFF" fontFamily="Arial, sans-serif" letterSpacing="0.6">AMERICAN</text><text x="24" y="22" textAnchor="middle" fontSize="6.5" fontWeight="900" fill="#FFFFFF" fontFamily="Arial, sans-serif" letterSpacing="0.6">EXPRESS</text><text x="24" y="30" textAnchor="middle" fontSize="5" fontWeight="800" fill="#FFFFFF" fontFamily="Arial, sans-serif" letterSpacing="1.2">AMEX</text></svg>
    </div>
    <div className="h-7 w-12 bg-white rounded-md flex items-center justify-center px-1" title="Discover">
      <svg viewBox="0 0 48 32" className="h-5 w-auto" aria-label="Discover"><text x="22" y="21" textAnchor="middle" fontSize="8" fontWeight="800" fill="#231F20" fontFamily="Arial, sans-serif">DISC</text><circle cx="40" cy="18" r="4" fill="#F76B1C" /></svg>
    </div>
  </div>
);

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const trust = [
    { icon: Truck, title: 'Free USA Shipping', text: 'USPS on every order' },
    { icon: RotateCcw, title: '30-Day Returns', text: 'Simple and no-hassle' },
    { icon: ShieldCheck, title: 'Secure Checkout', text: 'Encrypted payments' },
  ];

  return (
    <footer className="mt-20 bg-dark-900 text-dark-300">
      {/* Trust row */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-1 sm:grid-cols-3 gap-6">
          {trust.map((t) => (
            <div key={t.title} className="flex items-center gap-3 justify-center sm:justify-start">
              <div className="w-11 h-11 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-athletic-300"><t.icon className="w-5 h-5" /></div>
              <div><p className="text-white font-semibold text-sm">{t.title}</p><p className="text-dark-400 text-xs">{t.text}</p></div>
            </div>
          ))}
        </div>
      </div>

      {/* Columns */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-2 lg:grid-cols-12 gap-10">
        <div className="col-span-2 lg:col-span-5">
          <div className="[&_span]:text-white"><Logo variant="light" /></div>
          <p className="text-dark-400 text-sm leading-relaxed mt-5 mb-6 max-w-sm">A calm, no-fuss shop for quality bike parts, accessories and supplies &mdash; from safety and tools to ride tech and complete bikes, shipped fast and free across the USA.</p>
          <form className="flex max-w-sm gap-2" onSubmit={(e) => e.preventDefault()}>
            <input type="email" placeholder="Your email address" className="flex-1 px-4 py-2.5 rounded-lg text-dark-900 text-sm bg-white placeholder-dark-400 focus:outline-none" />
            <button type="submit" className="px-4 py-2.5 rounded-lg bg-athletic-600 text-white font-semibold text-sm flex items-center gap-2 hover:bg-athletic-500 transition-colors"><Send className="w-4 h-4" /></button>
          </form>
        </div>

        <div className="lg:col-span-3">
          <h4 className="text-white font-semibold text-sm mb-4">Shop</h4>
          <ul className="space-y-2.5">
            <li><Link to="/shop" className="text-dark-400 hover:text-athletic-300 text-sm transition-colors">All Products</Link></li>
            {categories.map((cat) => (<li key={cat.id}><Link to={`/shop?category=${cat.slug}`} className="text-dark-400 hover:text-athletic-300 text-sm transition-colors">{cat.name}</Link></li>))}
          </ul>
        </div>

        <div className="lg:col-span-2">
          <h4 className="text-white font-semibold text-sm mb-4">Help</h4>
          <ul className="space-y-2.5">
            {[{ name: 'Shipping', path: '/shipping' }, { name: 'Returns', path: '/returns' }, { name: 'FAQ', path: '/faq' }, { name: 'Privacy', path: '/privacy' }, { name: 'Terms', path: '/terms' }].map((link) => (<li key={link.path}><Link to={link.path} className="text-dark-400 hover:text-athletic-300 text-sm transition-colors">{link.name}</Link></li>))}
          </ul>
        </div>

        <div className="lg:col-span-2">
          <h4 className="text-white font-semibold text-sm mb-4">Contact</h4>
          <div className="space-y-3 text-sm">
            <a href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`} className="flex items-start gap-2 text-dark-400 hover:text-white transition-colors"><Phone className="w-4 h-4 mt-0.5 text-athletic-300" /> <span>{CONTACT_INFO.phone}</span></a>
            <a href={`mailto:${CONTACT_INFO.email}`} className="flex items-start gap-2 text-dark-400 hover:text-white transition-colors"><Mail className="w-4 h-4 mt-0.5 text-athletic-300" /> <span className="break-all">{CONTACT_INFO.email}</span></a>
            <div className="flex items-start gap-2 text-dark-400"><MapPin className="w-4 h-4 mt-0.5 text-athletic-300 flex-shrink-0" /><span>{CONTACT_INFO.address.street}<br />{CONTACT_INFO.address.city}, {CONTACT_INFO.address.state} {CONTACT_INFO.address.zip}</span></div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3 text-sm text-dark-400 text-center">
            <span>&copy; {currentYear} Cycle Market Direct</span>
            <span className="hidden sm:block text-white/15">|</span>
            <span>{CONTACT_INFO.corpName}</span>
          </div>
          <div className="flex items-center gap-3"><span className="text-dark-500 text-xs font-medium">We Accept</span><PaymentLogos /></div>
          <p className="text-xs text-dark-500">Charges show as &quot;<span className="font-semibold text-dark-300">{CARD_DESCRIPTOR}</span>&quot;</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
